import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FlowpilotWindowLayoutComponent } from '@core/components/sidebar/components/flowpilot/window/flowpilot-window-layout.component';
import {
  Bounds,
  Position,
} from '@core/components/sidebar/components/flowpilot/window/flowpilot-window-layout.component';
import { FlowWindowHeaderComponent } from '@core/components/sidebar/components/flowpilot/window/flowpilot-window-header.component';
import { WindowContentService } from '@core/components/sidebar/components/flowpilot/window/services/window-content.service';
import { FlowpilotChatPopUpComponent } from '@core/components/sidebar/components/flowpilot/window/content/components/pop-up/flowpilot-chat-pop-up.component';
import { FlowpilotTopbarComponent } from '@core/components/sidebar/components/flowpilot/window/content/components/topbar/flowpilot-topbar.component';
import { FlowpilotSidebarComponent } from '@core/components/sidebar/components/flowpilot/window/content/components/sidebar/flowpilot-sidebar.component';
import { FlowpilotSidebarService } from '@core/components/sidebar/components/flowpilot/window/content/services/flowpilot-sidebar.service';
import { FlowpilotPopUpService } from '@core/components/sidebar/components/flowpilot/window/content/services/flowpilot-pop-up.service';

@Component({
  selector: 'app-flowpilot-window',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FlowpilotWindowLayoutComponent,
    FlowWindowHeaderComponent,
    FlowpilotChatPopUpComponent,
    FlowpilotTopbarComponent,
    FlowpilotSidebarComponent,
  ],
  template: `
    <app-flowpilot-window-layout
      (windowMoved)="windowMove.emit($event)"
      (resizeMoved)="resizeMove.emit($event)">
      <app-flowpilot-window-header header (collapseWindow)="collapse.emit()" />

      <div
        content
        class="bg-dune-0 dark:bg-dune-1000 flex size-full max-w-full p-2">
        @if (width() >= SIDEBAR_MIN_WIDTH) {
          <app-flowpilot-sidebar />
        }

        <div class="flex size-full flex-col">
          <app-flowpilot-topbar
            #topBar
            [showSidebarToggle]="showSidebarToggle()"
            (sidebarIconClicked)="openSidebar()" />

          <div class="relative flex size-full">
            <!-- Add content here -->

            @if (enablePopUp()) {
              <div
                class="absolute -top-2.5 left-1.5 z-10000 h-3/4 overflow-hidden pt-2.5"
                (mouseenter)="popupHovered.set(true)"
                (mouseleave)="popupHovered.set(false)">
                <app-flowpilot-chat-pop-up />
              </div>
            }
          </div>
        </div>
      </div>
    </app-flowpilot-window-layout>
  `,
})
export class FlowpilotWindowComponent {
  /** INJECTIONS **/
  /****/
  private readonly windowContentService = inject(WindowContentService);

  /****/
  private readonly sidebarService = inject(FlowpilotSidebarService);

  /****/
  private readonly popUpService = inject(FlowpilotPopUpService);

  /** OUTPUTS **/
  /****/
  readonly collapse = output<void>();

  /****/
  readonly windowMove = output<Position>();

  /****/
  readonly resizeMove = output<Bounds>();

  /** PROPERTIES **/
  /****/
  protected readonly width = this.windowContentService.widthSignal;

  /****/
  protected readonly SIDEBAR_MIN_WIDTH = 750;

  /****/
  protected readonly sidebarState = this.sidebarService.stateSignal;

  /****/
  protected readonly popupHovered = signal(false);

  /****/
  private readonly topBar =
    viewChild.required<FlowpilotTopbarComponent>('topBar');

  /****/
  protected readonly enablePopUp = computed(() => {
    // when the sidebar is not closed the pop up should never be shown
    if (this.sidebarState() !== 'closed') return false;

    // open due to the icon in the top bar being hovered
    const hoveringIcon = this.topBar().sidebarIconHoveredSignal();

    // open because the mouse is inside the menu
    const hoveringPopup = this.popupHovered();

    // open because a menu of a chat inside the pop is open (needed because due to cdkPortal the mouse leave event fires when opening a menu)
    const menuInPopupOpen = this.popUpService.menuOpenSignal();

    return hoveringIcon || hoveringPopup || menuInPopupOpen;
  });

  /****/
  protected readonly showSidebarToggle = computed(() => {
    if (this.width() < this.SIDEBAR_MIN_WIDTH) return true;
    else return this.sidebarState() === 'closed';
  });

  protected openSidebar() {
    if (this.width() < this.SIDEBAR_MIN_WIDTH) return;
    this.sidebarService.openSidebar();
  }
}
